import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Crown, Loader2, ExternalLink, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { verifyPaymentSuccess } from '../utils/api';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshPremiumStatus } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  
  const sessionId = searchParams.get('session_id');
  const returnUrl = searchParams.get('return_url');
  const shoeId = searchParams.get('shoe_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setError('Invalid payment session');
        setLoading(false);
        return;
      }

      try {
        const result = await verifyPaymentSuccess(sessionId);
        
        if (result.success && result.data?.isPremium) {
          setVerified(true);
          // Refresh premium status in AuthContext
          await refreshPremiumStatus();
        } else if (result.data?.pending) {
          // Payment is being processed
          setVerified(true);
          // Try refreshing premium status a few times
          let attempts = 0;
          const maxAttempts = 5;
          const interval = setInterval(async () => {
            attempts++;
            await refreshPremiumStatus();
            if (attempts >= maxAttempts) {
              clearInterval(interval);
            }
          }, 2000);
        } else {
          setError(result.message || 'Payment verification failed');
        }
      } catch (err: any) {
        console.error('Payment verification error:', err);
        setError(err.message || 'Failed to verify payment');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, refreshPremiumStatus]);

  const handleContinue = () => {
    if (shoeId) {
      // Navigate to the specific shoe page
      navigate(`/shoe/${shoeId}`);
    } else if (returnUrl && returnUrl !== window.location.href) {
      // Navigate to the original page if it's not the current page
      try {
        // Extract path from full URL if needed
        const url = new URL(returnUrl);
        navigate(url.pathname + url.search);
      } catch {
        // If it's already a path, use it directly
        navigate(returnUrl);
      }
    } else {
      // Navigate to home
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Issue</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Premium!</h1>
          <p className="text-gray-600">Your subscription is now active</p>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-3 mb-3">
            <Crown className="h-6 w-6 text-amber-500" />
            <span className="font-semibold text-gray-900">You now have access to:</span>
          </div>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>All exclusive discount links</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>Save $50-$100 on every purchase</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>Unlimited shoe recommendations</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>Priority price drop alerts</span>
            </li>
          </ul>
        </div>

        {/* CTA Button */}
        <div className="space-y-3">
          <button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
          >
            <span>Start Shopping</span>
            <ArrowRight className="h-5 w-5" />
          </button>
          
          {shoeId && (
            <p className="text-center text-sm text-gray-500">
              Click above to return and complete your purchase
            </p>
          )}
        </div>

        {/* Email confirmation note */}
        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-500">
            A confirmation email has been sent to your email address.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;

